import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import type { TFetchError } from '@/api/api-error.types';
import { _moveInvestmentPipelineCard } from '@/api/pipelines/mutations';
import {
  _getInvestmentPipelineBoard,
  _getInvestmentPipelineCardDetail,
} from '@/api/pipelines/queries';
import { useToast } from '@/design-system';
import {
  isPipelinePermissionError,
  PipelineBoard,
  PipelineBoardRegion,
  PipelineBoardStack,
  PipelineBoardState,
  PipelineDetailDrawer,
  PipelineFiltersBar,
  usePipelineCardMove,
} from '@/pages/pipelines/common';
import { togglePipelineArrayFilterValue } from '@/pages/pipelines/common/utils/url';
import { getApiErrorMessage } from '@/utils/api-error-message';

import {
  getInvestmentPipelineApiSort,
  getInvestmentPipelineBoardSort,
  INVESTMENT_PIPELINE,
  INVESTMENT_PIPELINE_API_SORT,
} from './config';
import { useInvestedApproval } from './hooks/use-invested-approval';
import { useInvestmentPipelineUrlState } from './hooks/use-investment-pipeline-url-state';
import { ConfirmInvestmentDialog } from './modules/confirm-investment-dialog';
import { getInvestmentPipelineFilterDefinitions } from './utils/filters';
import { mapInvestmentBoardToPipelineBoard } from './utils/map-board';
import { mapInvestmentEntryToPipelineCardDetail } from './utils/map-detail';
import { normalizeInvestmentPipelineBoard } from './utils/normalize-board';
import { normalizeInvestmentPipelineDetail } from './utils/normalize-detail';
import { toInvestmentPipelineBoardFilters } from './utils/url';

export const InvestmentPipeline = () => {
  const {
    filters,
    activeFilterCount,
    selectedCardId,
    setFilters,
    resetFilters,
    selectCard,
    closeCard,
  } = useInvestmentPipelineUrlState();
  const boardSort = useMemo(() => getInvestmentPipelineBoardSort(filters.sort), [filters.sort]);
  const apiSort = getInvestmentPipelineApiSort(boardSort);
  const boardFilters = useMemo(() => toInvestmentPipelineBoardFilters(filters), [filters]);
  const boardQueryOptions = useMemo(
    () => _getInvestmentPipelineBoard({ ...boardFilters, sort: apiSort }),
    [apiSort, boardFilters],
  );
  const boardQuery = useQuery(boardQueryOptions);
  const board = useMemo(
    () =>
      boardQuery.data
        ? normalizeInvestmentPipelineBoard(
            mapInvestmentBoardToPipelineBoard(boardQuery.data),
            boardFilters,
          )
        : undefined,
    [boardFilters, boardQuery.data],
  );
  const filterDefinitions = useMemo(
    () => getInvestmentPipelineFilterDefinitions(board?.filterDefinitions ?? []),
    [board?.filterDefinitions],
  );
  const detailQuery = useQuery(_getInvestmentPipelineCardDetail(selectedCardId));
  const detail = useMemo(
    () =>
      detailQuery.data
        ? normalizeInvestmentPipelineDetail(
            mapInvestmentEntryToPipelineCardDetail(detailQuery.data),
          )
        : undefined,
    [detailQuery.data],
  );
  const { add: addToast } = useToast();
  const moveCardMutation = useMutation({
    mutationFn: _moveInvestmentPipelineCard,
    onError: (error) =>
      addToast({
        title: 'Could not move the card',
        description: getApiErrorMessage(error as TFetchError, 'Please try again.'),
        data: { status: 'error' },
      }),
  });
  const approval = useInvestedApproval({
    movePipelineCard: moveCardMutation.mutateAsync,
    findPledged: (cardId) =>
      boardQuery.data?.columns
        .flatMap((column) => column.entries)
        .find((entry) => entry.id === cardId)?.pledged ?? null,
    onRefuse: () =>
      addToast({
        title: 'Cannot approve this card',
        description: 'There is no subscription behind it, so there is no amount to confirm.',
        data: { status: 'error' },
      }),
  });
  const moveCard = usePipelineCardMove({
    pipeline: INVESTMENT_PIPELINE,
    movePipelineCard: approval.movePipelineCard,
  });

  const toggleStageFilter = useCallback(
    (stage: string) => {
      setFilters(togglePipelineArrayFilterValue(filters, 'stages', stage));
    },
    [filters, setFilters],
  );

  const toggleSort = useCallback(() => {
    const nextBoardSort = getInvestmentPipelineBoardSort(
      boardSort.mode === 'lastStageChanged'
        ? INVESTMENT_PIPELINE_API_SORT.urgencyThenLastStageChanged
        : INVESTMENT_PIPELINE_API_SORT.lastStageChanged,
    );
    setFilters({ ...filters, sort: getInvestmentPipelineApiSort(nextBoardSort) });
  }, [boardSort, filters, setFilters]);

  return (
    <PipelineBoardStack>
      <PipelineFiltersBar
        filters={filters}
        filterDefinitions={filterDefinitions}
        onChange={setFilters}
        onReset={resetFilters}
      />

      <PipelineBoardRegion>
        <PipelineBoardState
          board={board}
          loading={boardQuery.isLoading}
          permissionError={isPipelinePermissionError(boardQuery.error)}
          fetchPaused={boardQuery.fetchStatus === 'paused' && !boardQuery.data}
          error={boardQuery.isError}
          hasActiveFilters={activeFilterCount > 0}
          onRetry={boardQuery.refetch}
        >
          {(readyBoard) => (
            <PipelineBoard
              board={readyBoard}
              filteredStages={filters.stages}
              sort={boardSort}
              activeOnly={filters.activeOnly}
              movePending={moveCardMutation.isPending || Boolean(approval.pendingApproval)}
              onMoveCard={moveCard}
              onSelectCard={selectCard}
              onToggleStageFilter={toggleStageFilter}
              onToggleSort={toggleSort}
            />
          )}
        </PipelineBoardState>
      </PipelineBoardRegion>

      {approval.pendingApproval && (
        <ConfirmInvestmentDialog
          open
          pledged={approval.pendingApproval.pledged}
          isSubmitting={moveCardMutation.isPending}
          onConfirm={approval.confirm}
          onCancel={approval.cancel}
        />
      )}

      <PipelineDetailDrawer
        open={Boolean(selectedCardId)}
        detail={detail}
        loading={detailQuery.isLoading}
        error={detailQuery.isError}
        onClose={closeCard}
      />
    </PipelineBoardStack>
  );
};
